import updateServerTimeDelta from "@services/updateServerTimeDelta";
import { dev } from "@utils/dev";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CanceledError,
  InternalAxiosRequestConfig,
} from "axios";
import { Buffer } from "buffer";
import { IS_DEBUG, REACT_APP_BASE_URL, WITH_CREDENTIALS } from "../../config";
import { serverErrors } from "../../i18n/serverErrors.messages";
import ServerErrorCodes from "./ServerErrorCodes";
import {
  BaseResponse,
  BaseResponseLowerCase,
  RecordResponse,
} from "./responses/BaseResponse";
import ResponseUtils from "./responses/ResponseUtils";
import requestInterceptor from "./requestInterceptor";

export class ErrorData {
  public code: number;
  public message: string;
  public status?: number;

  constructor(code: number, message: string, status?: number) {
    this.code = code;
    this.message = code ? serverErrors(code) : message;
    this.status = status;
    if (code === ServerErrorCodes.Unknown && status === undefined) {
      this.status = +100500; // иначе не будет тоста! там проверка на ненулевой статус
    }
  }

  public static create(
    response: BaseResponse | BaseResponseLowerCase
  ): ErrorData {
    const message = "Unknown error";
    const baseResponse = response as BaseResponse;
    if (baseResponse.Errcode !== undefined) {
      return new ErrorData(
        baseResponse.Errcode,
        baseResponse.Message ?? message
      );
    }
    const baseResponseLC = response as BaseResponseLowerCase;
    if (baseResponseLC.errcode !== undefined) {
      return new ErrorData(
        baseResponseLC.errcode,
        baseResponseLC.message ?? message
      );
    }
    // if (typeof response.Errcode !== "undefined") {
    //   return new ErrorData(response.Errcode, response.Message ?? message);
    // }
    return new ErrorData(ServerErrorCodes.Unknown, message);
  }

  public static unknown(e?: any): ErrorData {
    if (typeof e !== "undefined") {
      dev.log("Error: ", e);
      return new ErrorData(0, `${e}`);
    }
    return new ErrorData(ServerErrorCodes.Unknown, "Unknown error");
  }

  public static cancelled(): ErrorData {
    return new ErrorData(ServerErrorCodes.Canceled, "Canceled request");
  }
}

function responseInterceptor(response: AxiosResponse<any>) {
  if (response.headers.date) {
    updateServerTimeDelta(response.headers.date);
  }
  const result = {
    ...response,
    data: {} as any,
  };
  let rawData = {} as any;
  if (response.data.Base64) {
    rawData = JSON.parse(
      Buffer.from(response.data.Base64, "base64").toString()
    );
  } else {
    rawData = { ...response.data };
  }
  const keys = Object.keys(rawData);
  for (let i = 0; i < keys.length; i++) {
    const fieldName = `${keys[i][0].toUpperCase()}${keys[i]
      .substring(1)
      .toLowerCase()}`;
    result.data[fieldName] = rawData[keys[i]];
  }

  const meta: string = result.data.Record?.meta;

  if (meta) {
    const parsed = JSON.parse(
      replaceIncorrectJSONSymbols(result.data.Record?.meta || "{}")
    );
    result.data = {
      ...result.data,
      Record: { ...result.data.Record, meta: parsed },
    };
  } else if (result.data.Records) {
    result.data = {
      ...result.data,
      Records: result.data.Records.map((item: any) => {
        if (item.meta) {
          return {
            ...item,
            meta: JSON.parse(replaceIncorrectJSONSymbols(item.meta || "{}")),
          };
        }
        if (Array.isArray(item)) {
          return item;
        }
        if (typeof item === "object") {
          return {
            ...item,
            meta: {},
          };
        }
        return item;
      }),
    };
  }
  //if (IS_DEBUG) { так же по просьбе
  // добавил по просьбе ed
  (window as any).lastanswer = rawData;
  //}
  return result;
}

function replaceIncorrectJSONSymbols(value: string): string {
  return value.replaceAll(/[\t\n]/g, "");
}

function makeConfig(params: AxiosRequestConfig): AxiosRequestConfig {
  const defaults: AxiosRequestConfig = {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    },
    withCredentials: WITH_CREDENTIALS,// !(process.env.NODE_ENV === 'development')
  }
  return {
    ...params,
    ...defaults,
  }
}

// const authParams = (token) =>
//   TESTING_PARAMS ? `Basic ${window.btoa(TESTING_PARAMS)}, Bearer ${token}` : `Bearer ${token}`;


export class HttpService {
  private _axios: AxiosInstance;

  constructor(baseURL: string, useLogs: boolean = false) {


    this._axios = axios.create({
      baseURL: baseURL,
    });

    //console.log("console.log(axios.defaults.adapter);", axios.defaults.adapter);

    this._axios.interceptors.response.use(responseInterceptor);
    this._axios.interceptors.request.use(requestInterceptor);

    if (process.env.NODE_ENV === "development") {
      if (useLogs) {
        this._axios.interceptors.request.use(
          (request: InternalAxiosRequestConfig) => {
            //console.log('Request', JSON.stringify(request, null, 2))
            return request;
          }
        );
        this._axios.interceptors.response.use((response) => {
          //console.log('Response:', JSON.stringify(response, null, 2))
          return response;
        });
      }
    }
  }

  private async call<T extends BaseResponse | BaseResponseLowerCase>(
    api: () => Promise<AxiosResponse<T>>
  ): Promise<T | ErrorData> {
    try {
      const response = await api();
      if (response.headers["content-type"] === "text/html") {
        return Promise.reject(
          new ErrorData(
            ServerErrorCodes.Unknown,
            "Response is not JSON",
            response.status
          )
        );
      }
      //тут мы смотрим пришла ли ошибка с пояснениями в Record
      if (
        ResponseUtils.isErrorWithResponse(
          response.data as unknown as RecordResponse<any>
        )
      ) {
        //и далее уже обрабатываем ответ внутри компонента
        return Promise.resolve(response.data);
      }

      // //тут мы смотрим пришла ли ошибка с пояснениями в Record
      // if (
      //   ResponseUtils.isErrorWithResponse(
      //     response.data as unknown as RecordResponse<any>
      //   )
      // ) {
      //   //и далее уже обрабатываем ответ внутри компонента
      //   return Promise.resolve(response.data);
      // }

      if (ResponseUtils.isError(response.data)) {
        // так как код (isSuccessWithBlob)
        // просто смотрит http status,
        // проверку на ошибку необходимо поместить выше!
        return Promise.reject(ErrorData.create(response.data));
      }
      if (ResponseUtils.isSuccess(response.data)) {
        return Promise.resolve(response.data);
      }
      if (ResponseUtils.isSuccessWithNumStatus(response)) {
        // вызывает вопросы на Blob проверки нет, не логично
        return Promise.resolve(response.request.response);
      }
      return Promise.reject(
        new ErrorData(ServerErrorCodes.Unknown, "Unknown response")
      );
    } catch (e) {
      if (e instanceof CanceledError) {
        return Promise.reject(ErrorData.cancelled());
      }
      // server error 404, 405, 500 etc
      if (e instanceof Error) {
        if (e instanceof AxiosError) {
          if (e.status === 413) {
            return Promise.reject(new ErrorData(413, "File is too large", 413));
          }
        }
        return Promise.reject(new ErrorData(0, e.message));
      }
      if (typeof e === "string") {
        return Promise.reject(new ErrorData(-2, e));
      }
      return Promise.reject(ErrorData.unknown(e));
    }
  }

  put = async <T extends BaseResponse>(
    URL: string,
    payload: any,
    abortSignal?: AbortSignal
  ): Promise<T | ErrorData> => {
    return this.call(() =>
      this._axios.put<T>(URL, payload, makeConfig({ signal: abortSignal }))
    );
  };

  get = async <T extends BaseResponse>(
    URL: string,
    abortSignal?: AbortSignal,
    payload?: any
  ): Promise<T | ErrorData> => {
    return this.call(() =>
      this._axios.get<T>(URL, makeConfig({
        signal: abortSignal,
        params: payload
      }))
    );
  };

  getBlob = async (URL: string, abortSignal?: AbortSignal): Promise<Blob> => {
    const response = await this._axios.get<Blob>(URL, makeConfig({
      signal: abortSignal,
      responseType: "blob",
    }));
    return response.request.response; // какой-то баг axios? почему-то не в data
  };

  post = async <T extends BaseResponse>(
    URL: string,
    payload: any,
    abortSignal?: AbortSignal,
    blob?: boolean
  ): Promise<T | ErrorData> => {
    return this.call(() =>
      this._axios.post<T>(URL, payload, makeConfig({
        signal: abortSignal,
        responseType: blob ? "blob" : undefined,
      }))
    );
  };

  delete = <T extends BaseResponse>(
    URL: string,
    abortSignal?: AbortSignal
  ): Promise<T | ErrorData> => {
    return this.call(() => this._axios.delete<T>(URL, makeConfig({
      signal: abortSignal
    })));
  };

  postJson = async <T extends BaseResponse>(
    URL: string,
    payload: any,
    abortSignal?: AbortSignal
  ): Promise<T | ErrorData> => {
    let config = makeConfig({
      signal: abortSignal,
    });
    config.headers = {
      ...config.headers,
      "Content-Type": "application/json"
    };
    console.log("config.headers", config.headers);
    return this.call(() =>
      this._axios.post<T>(URL, payload, config)
    );
  }
}

const httpService = new HttpService(REACT_APP_BASE_URL + "/api", true);
export default httpService;
